import React from 'react';
import { cn } from '../../lib/utils';

export interface TailwindSliderProps {
  value?: number;
  onChange?: (value: number) => void;
  onChangeCommitted?: (value: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  valueLabelDisplay?: 'auto' | 'on' | 'off';
  valueLabelFormat?: (value: number) => string;
  className?: string;
}

export default function TailwindSlider({
  value = 0,
  onChange,
  onChangeCommitted,
  disabled = false,
  min = 0,
  max = 100,
  step = 1,
  valueLabelDisplay = 'auto',
  valueLabelFormat,
  className,
}: TailwindSliderProps) {
  const [isDragging, setIsDragging] = React.useState(false);

  const percentage = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(parseFloat(e.target.value));
  };

  const handlePointerUp = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setIsDragging(false);
    onChangeCommitted?.(parseFloat((e.target as HTMLInputElement).value));
  };

  const handlePointerDown = () => {
    setIsDragging(true);
  };

  const showLabel = valueLabelDisplay === 'on' || (valueLabelDisplay === 'auto' && isDragging);
  const displayValue = valueLabelFormat ? valueLabelFormat(value) : value;

  return (
    <div className={cn('relative pt-5', className)}>
      {/* Value label */}
      {showLabel && (
        <div
          className="absolute top-0 transform -translate-x-1/2 text-xs font-medium text-slate-600 whitespace-nowrap"
          style={{
            left: `${percentage}%`,
          }}
        >
          {displayValue}
        </div>
      )}

      {/* Slider input with gradient background for track fill */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleChange}
        onMouseUp={handlePointerUp}
        onMouseDown={handlePointerDown}
        onTouchEnd={handlePointerUp}
        onTouchStart={handlePointerDown}
        disabled={disabled}
        style={{
          background: `linear-gradient(to right, #135bec ${percentage}%, rgb(226, 232, 240) ${percentage}%)`,
        }}
        className={cn(
          'w-full h-2 rounded-lg appearance-none cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          '[&::-webkit-slider-thumb]:appearance-none',
          '[&::-webkit-slider-thumb]:w-5',
          '[&::-webkit-slider-thumb]:h-5',
          '[&::-webkit-slider-thumb]:rounded-full',
          '[&::-webkit-slider-thumb]:bg-primary',
          '[&::-webkit-slider-thumb]:cursor-pointer',
          '[&::-webkit-slider-thumb]:transition-all',
          '[&::-webkit-slider-thumb]:hover:scale-110',
          '[&::-webkit-slider-thumb]:disabled:bg-slate-400',
          '[&::-moz-range-thumb]:w-5',
          '[&::-moz-range-thumb]:h-5',
          '[&::-moz-range-thumb]:rounded-full',
          '[&::-moz-range-thumb]:bg-primary',
          '[&::-moz-range-thumb]:border-0',
          '[&::-moz-range-thumb]:cursor-pointer',
          '[&::-moz-range-thumb]:transition-all',
          '[&::-moz-range-thumb]:hover:scale-110',
          '[&::-moz-range-thumb]:disabled:bg-slate-400',
          'focus:outline-none'
        )}
      />
    </div>
  );
}
